/**
 * WaitingChip — urgency indicator for approval queue rows.
 * Variant drives color: urgent=error, mid=warning, ok=muted.
 */
import { cn } from "@/shared/lib/utils";

export type WaitingChipVariant = "urgent" | "mid" | "ok";

interface WaitingChipProps {
  days: number;
  variant: WaitingChipVariant;
  /** Override label text; defaults to days + urgency suffix. */
  label?: string;
  className?: string;
}

const VARIANT_STYLES: Record<WaitingChipVariant, { bg: string; text: string }> = {
  urgent: { bg: "var(--error-bg, rgba(184,76,58,0.1))",   text: "var(--error-text, #B84C3A)" },
  mid:    { bg: "var(--warning-bg, rgba(198,124,58,0.1))", text: "var(--warning-text, #8B4C2A)" },
  ok:     { bg: "var(--color-surface-low)",               text: "var(--text-tertiary)" },
};

const VARIANT_SUFFIX: Record<WaitingChipVariant, string> = {
  urgent: " — urgent",
  mid:    " — review soon",
  ok:     "",
};

export function WaitingChip({ days, variant, label, className }: WaitingChipProps) {
  const { bg, text } = VARIANT_STYLES[variant];
  const displayLabel = label ?? `${days}d${VARIANT_SUFFIX[variant]}`;

  return (
    <span
      className={cn("inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10.5px] font-semibold", className)}
      style={{ background: bg, color: text }}
    >
      {displayLabel}
    </span>
  );
}
